/* Uit Krabbelpaal.dc.html; bodies letterlijk overgenomen. */

import { api, asJson } from '../lib/api.js';
import { BUZZ_COOLDOWN, BUZZ_PREFIX, PULL_MAX, PULL_TRIGGER } from '../lib/config.js';
import { isBuzzText, playBlip, playBuzz, playPop } from '../lib/sound.js';
import { MENTION_TAIL, byName, mentionSplit } from '../lib/text.js';
import * as store from '../lib/store.js';

export default {
  /**
   * Hoeveel berichten je nog niet gezien hebt. Alleen die van anderen.
   *
   * Dit ging op lengte minus gelezen, en dat klopte zolang de chat vast naast de
   * paal stond: hij was zichtbaar, dus markRead liep meteen na elk bericht. Nu is
   * de chat een venster dat dicht kan staan terwijl je in de taakbalk typt, en dan
   * zette je eigen berichtje het belletje op 1 en de paginatitel op "(1)".
   *
   * Niet oplossen door na het versturen alles als gelezen te markeren: komt er in
   * diezelfde ophaalronde een bericht van een collega binnen, dan zou je dat stil
   * wegstrepen. Alleen de jouwe eruit filteren kan dat niet.
   *
   * Eén functie voor het belletje én de paginatitel, want die stonden elk hun eigen
   * som te maken en daar zat het verschil.
   */
  unreadFromOthers() {
      const vanMij = (m) => (m.accountId ? m.accountId === this.state.accountId : m.author === this.state.author);
      /**
       * Op tijd, niet op plaats in de lijst.
       *
       * Dit was een aantal: "de eerste zoveel heb je gezien". Maar de chat haalt de
       * nieuwste 200 op, dus boven de 200 blijft de lengte gelijk als er een bericht
       * bijkomt, en dan stond het belletje voorgoed op nul. Het tijdstip van het
       * laatste bericht dat je zag schuift niet mee.
       */
      const sinds = this.state.seenAt || '';
      return (this.state.chatMessages || []).filter((m) => m.created > sinds && !vanMij(m)).length;
    },

  markRead(cb) {
      const msgs = this.state.chatMessages || [];
      const at = msgs.length ? msgs[msgs.length - 1].created || '' : this.state.seenAt || '';
      store.set('gp_chat_seen_at', at);
      this.setState({ seenAt: at }, cb);
    },

  // De chat is weer een venster dat je opent vanuit de taakbalk. Hij stond een
  // tijd vast naast de paal; die plek is nu van je eigen cijfers.
  chatVisible() { return this.state.chatOpen; },

  toggleChat() {
      const opening = !this.state.chatOpen;
      this.setState({ chatOpen: opening, emojiOpen: false, notifOpen: opening ? false : this.state.notifOpen }, () => {
        if (opening) this.markRead(() => requestAnimationFrame(() => this.scrollChat()));
      });
    },

  /*
   * Hier stond de sleepcode van het chatvenster: drie functies met een eigen
   * greep, eigen luisteraars op `window` en een eigen plek in de staat.
   *
   * Dat was de derde kopie van hetzelfde. De vensterlaag doet het nu ook voor de
   * chat: dezelfde greep, dezelfde regels over in beeld blijven, dezelfde plek in
   * `state.vensterPlek`. Zie logic/venster.js, en sectie 12 van CLAUDE.md.
   *
   * Wat het verschil maakte: de oude code luisterde op de muis en de nieuwe op de
   * pointer, met `setPointerCapture`. Daarmee blijft het slepen bij je muis ook als
   * je te snel over de rand van het venster gaat, en werkt het op een tekentablet.
   */

  toggleEmoji() { return this.setState({ emojiOpen: !this.state.emojiOpen }, () => requestAnimationFrame(() => this.scrollChat())); },

  addEmoji(ch) { return this.setState({ chatInput: this.state.chatInput + ch }); },

  onChatInput(e) {
      const v = e.target.value;
      const caret = typeof e.target.selectionStart === 'number' ? e.target.selectionStart : v.length;
      const m = MENTION_TAIL.exec(v.slice(0, caret));
      this.setState({ chatInput: v, mentionQuery: m ? m[1] : null });
    },

  onPullStart(e) {
      this.pullFrom = null;
      if (!this.state.isMobile || this.state.pullBusy) return;
      if (this.state.chatOpen || this.state.notifOpen || this.state.dialog) return;
      if (e.currentTarget.scrollTop > 2) return;
      this.pullFrom = e.touches[0].clientY;
    },

  onPullMove(e) {
      if (this.pullFrom == null) return;
      if (e.currentTarget.scrollTop > 2) { this.pullFrom = null; if (this.state.pullY) this.setState({ pullY: 0 }); return; }
      const dy = e.touches[0].clientY - this.pullFrom;
      // Omhoog vegen is gewoon scrollen: laat de paal met rust tot je duidelijk
      // naar beneden trekt, anders hapert elke veeg op een hertekening.
      if (dy < 10) { if (this.state.pullY) this.setState({ pullY: 0 }); return; }
      const y = Math.round(Math.min(PULL_MAX, (dy - 10) * 0.55));
      if (y !== (this.state.pullY || 0)) this.setState({ pullY: y });
    },

  onPullEnd() {
      const y = this.state.pullY || 0;
      this.pullFrom = null;
      if (y < PULL_TRIGGER) { if (y) this.setState({ pullY: 0 }); return; }
      this.pullRefresh();
    },

  // Verversen met een zichtbaar rondje: ook de knop "De paal" gebruikt dit.
  pullRefresh() {
      if (this.state.pullBusy) return;
      this.setState({ pullY: 54, pullBusy: true });
      playPop();
      const el = this.galleryScrollRef.current;
      if (el && el.scrollTop > 0) el.scrollTo({ top: 0, behavior: 'smooth' });
      Promise.resolve(this.refreshGallery(true)).catch(() => {}).then(() => {
        setTimeout(() => this.setState({ pullY: 0, pullBusy: false }), 420);
      });
    },

  mentionMatches() {
      const q = this.state.mentionQuery;
      if (!this.state.buzzPick && (q === null || q === undefined)) return [];
      const s = (q || '').toLowerCase();
      return (this.state.accounts || [])
        .filter((a) => a.name && a.id !== this.state.accountId && (!s || a.name.toLowerCase().includes(s)))
        .slice()
        .sort(byName);
    },

  pickMention(name) {
      const v = this.state.chatInput;
      const next = MENTION_TAIL.test(v) ? v.replace(MENTION_TAIL, '@' + name + ' ') : v + '@' + name + ' ';
      this.setState({ chatInput: next, mentionQuery: null });
    },

  onChatKey(e) {
      const opts = this.mentionMatches();
      if (opts.length && (e.key === 'Enter' || e.key === 'Tab')) { e.preventDefault(); this.pickMention(opts[0].name); return; }
      // Alleen de lijst dicht. Zonder stopPropagation kwam dezelfde Escape daarna bij
      // handleKey uit en ging ook de chat zelf dicht.
      if (e.key === 'Escape' && this.state.mentionQuery !== null) { e.stopPropagation(); this.setState({ mentionQuery: null }); return; }
      if (e.key === 'Enter') { e.preventDefault(); this.sendChat(); }
    },

  startChatPoll() {
      this.stopChatPoll();
      this.chatTick = 0;
      // Poll only when the tab is visible: open chat every 3s, closed chat every 15s.
      this.chatTimer = setInterval(() => {
        if (document.hidden) return;
        const n = this.chatTick++;
        if (!this.chatVisible() && n % 5 !== 0) return;
        // Met de lijn open komt een bericht binnen zodra het verstuurd wordt. Deze
        // timer is dan alleen nog een vangnet: elke halve minuut een keer kijken.
        if (this.liveSkip(n, 10)) return;
        this.fetchChat();
      }, 3000);
    },

  stopChatPoll() { if (this.chatTimer) { clearInterval(this.chatTimer); this.chatTimer = null; } },

  scrollChat() {
      const el = this.chatScrollRef.current;
      if (el) el.scrollTop = el.scrollHeight;
    },

  async fetchChat() {
      if (!this.state.connected) return;
      try {
        // Nieuwste eerst ophalen en omdraaien: anders bleef de chat bij meer dan
        // 200 berichten hangen op de oudste pagina en kwam er niets nieuws binnen.
        // Altijd de nieuwste eerst opvragen; id als tiebreak zodat berichten met
        // dezelfde seconde niet door elkaar heen schuiven.
        const res = await api(`${this.state.pbUrl}/api/collections/paal_messages/records?sort=-created,-id&perPage=200`);
        if (!res.ok) return;
        const data = await res.json();
        // Met het accountId erbij: de naam alleen gaat scheef zodra iemand zich anders
        // noemt, en dan stonden een ander zijn naam en gezicht bij jouw oude berichten.
        const msgs = (data.items || []).map((r) => ({ id: r.id, author: r.author || 'onbekend', accountId: r.accountId || '', text: r.text || '', created: r.created || '' })).reverse();
        const last = msgs[msgs.length - 1];
        const isNew = !!last && last.id !== this.lastMsgId;
        // Bij een venster van 200 blijft de lengte gelijk; nieuw = een ander laatste bericht.
        const grew = isNew && this.chatLoaded ? true : msgs.length > this.lastMsgLen;
        this.lastMsgLen = msgs.length;
        this.lastMsgId = last ? last.id : '';
        const vanMij = last && (last.accountId ? last.accountId === this.state.accountId : last.author === this.state.author);
        if (grew && isNew && this.chatLoaded && !vanMij) {
          const my = (this.state.author || '').toLowerCase();
          // Een hele naam, geen stukje: wie Jan heet kreeg ook de buzz voor @Janneke.
          // Dezelfde splitsing als de weergave, zodat wat oplicht ook is wat piept.
          const forMe = !!my && mentionSplit(last.text, this.mentionNames, my).some((p) => p.me);
          if (forMe && isBuzzText(last.text)) this.receiveBuzz(last.author);
          else {
            playBlip();
            if (forMe && !this.chatVisible()) this.notify(last.author + ' noemde je in de chat!');
          }
        }
        this.chatLoaded = true;
        // Nog nooit iets gezien (een verse browser, of van voor deze teller): dan geldt
        // wat er nu staat als gelezen, in plaats van tweehonderd ongelezen berichten.
        const eerst = !this.state.seenAt && last ? last.created : '';
        if (eerst) store.set('gp_chat_seen_at', eerst);
        this.setState(eerst ? { chatMessages: msgs, seenAt: eerst } : { chatMessages: msgs }, () => {
          if (this.chatVisible()) this.markRead(() => { if (grew) this.scrollChat(); });
        });
      } catch (e) {}
    },

  receiveBuzz(from) {
      playBuzz();
      this.notify('⚡ ' + (from || 'iemand') + ' buzzt je!');
      if (this.buzzTimer) clearTimeout(this.buzzTimer);
      // Een buzz wil gezien worden, dus de chat komt bovenop en niet onder de stapel.
      this.vensterNaarVoren('chat');
      this.setState({ chatOpen: true, buzzing: true }, () => requestAnimationFrame(() => this.scrollChat()));
      this.buzzTimer = setTimeout(() => this.setState({ buzzing: false }), 1100);
    },

  buzzTarget() {
      const v = this.state.chatInput || '';
      const names = this.mentionNames;
      for (let i = 0; i < v.length; i++) {
        if (v[i] !== '@') continue;
        for (const n of names) {
          if (n && v.substr(i + 1, n.length).toLowerCase() === n.toLowerCase() && n !== this.state.author) return n;
        }
      }
      return '';
    },

  // De knop zet een ⚡ in het bericht en opent meteen de namenlijst.
  startBuzz() {
      if (!this.ensureName('Log in of maak een account aan om iemand te buzzen.')) return;
      if (this.state.buzzPick) { this.setState({ buzzPick: false, mentionQuery: null }); return; }
      playPop();
      this.setState({ buzzPick: true, mentionQuery: '', emojiOpen: false });
    },

  async sendBuzz(who) {
      const name = who || this.buzzTarget();
      if (this.state.chatSending) return;
      if (!this.ensureName('Log in of maak een account aan om iemand te buzzen.')) return;
      if (!name) { this.notify('Kies wie je wil buzzen.'); this.setState({ buzzPick: true, mentionQuery: '' }); return; }
      let last = 0;
      try { last = Number(store.get('gp_buzz_at') || 0); } catch (e) {}
      const wait = BUZZ_COOLDOWN - (Date.now() - last);
      if (wait > 0) {
        this.notify('Rustig aan! Je kunt over ' + Math.ceil(wait / 60000) + ' minuut' + (Math.ceil(wait / 60000) === 1 ? '' : 'en') + ' weer buzzen.');
        return;
      }
      try { store.set('gp_buzz_at', String(Date.now())); } catch (e) {}
      this.setState({ chatSending: true, mentionQuery: null, buzzPick: false, buzzing: true });
      playBuzz();
      if (this.buzzTimer) clearTimeout(this.buzzTimer);
      this.buzzTimer = setTimeout(() => this.setState({ buzzing: false }), 1100);
      try {
        const res = await api(`${this.state.pbUrl}/api/collections/paal_messages/records`, asJson('POST', { author: this.state.author, accountId: this.state.accountId || '', text: BUZZ_PREFIX + ' @' + name }));
        if (!res.ok) throw new Error('send failed');
        // Ook een buzz is een bericht, en het schudden van het venster is nogal het
        // punt ervan. Wie hem ontvangt ziet het venster ook openklappen; zie receiveBuzz.
        this.setState({ chatInput: '', chatSending: false, emojiOpen: false, chatOpen: true, notifOpen: false }, () => this.fetchChat());
      } catch (e) {
        this.setState({ chatSending: false });
        this.notify('De buzz kon niet verstuurd worden. Staat de server aan?');
      }
    },

  async sendChat() {
      const text = this.state.chatInput.trim();
      if (!text || this.state.chatSending) return;
      if (!this.ensureName('Log in of maak een account aan om mee te kletsen.')) return;
      if (isBuzzText(text)) { this.sendBuzz(); return; }
      this.setState({ chatSending: true });
      try {
        const res = await api(`${this.state.pbUrl}/api/collections/paal_messages/records`, asJson('POST', { author: this.state.author, accountId: this.state.accountId || '', text }));
        if (!res.ok) throw new Error('send failed');
        // Het venster gaat open zodra je iets verstuurt. Typ je vanuit de taakbalk,
        // dan zag je anders alleen je veld leeglopen en verder niets: geen bericht,
        // geen bevestiging, niets. fetchChat merkt vervolgens dat de chat zichtbaar
        // is en scrollt naar je eigen regel toe.
        this.setState({ chatInput: '', mentionQuery: null, chatSending: false, emojiOpen: false, chatOpen: true, notifOpen: false }, () => this.fetchChat());
      } catch (e) {
        this.setState({ chatSending: false });
        this.notify('Je bericht kon niet verstuurd worden. Staat de server aan?');
      }
    }
};
